import React, { useCallback, useMemo } from "react";
import { Tooltip, Button } from "@sellix/shared/components";
import Table from "../../components/table";
import Empty from "../../components/empty";
import { getCDNLink } from "../../utils/config";
import { CopyIcon, PureFileIcon } from "../../assets/icons";
import { useTranslation } from "react-i18next";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { CustomerInfo, ProductNormalized } from "../../types/types";
import { getRevenueAmount, getRevenuePercent } from "./mobile-products";
import { useAuthContext } from "../../provider/AuthContext.ts";

import "./index.css";

export interface IVariant {
    description: string
    price: number
    title: string
}

interface AffiliateTableProps {
    customerInfo: CustomerInfo
    data: ProductNormalized[]
    isApproved: boolean
}

const AffiliateTable = ({ data, customerInfo, isApproved }: AffiliateTableProps) => {

  const { t } = useTranslation('translation', { keyPrefix: 'customer.affiliates' });
  const { customerPortalDomain } = useAuthContext();

  const createPrice = useCallback(({ row: { original } }: CellContext<ProductNormalized, unknown>) => {
    return getRevenueAmount(original, customerInfo.shop.affiliate_revenue_percent, customerInfo.customer.affiliate_revenue_percent);
  }, [customerInfo]);

  const createBalance = useCallback(({ row: { original } }: CellContext<ProductNormalized, unknown>) => {
    return getRevenuePercent(original.affiliate_revenue_percent, customerInfo.shop.affiliate_revenue_percent, customerInfo.customer.affiliate_revenue_percent) + "%";
  }, [customerInfo]);

  const createProduct = useCallback(({ row: { original } }: CellContext<ProductNormalized, unknown>) => {
    return <div className="product-table-type">
      <div className="product-table-type-icon" >{original.cloudflare_image_id ? <img src={getCDNLink({ id: original.cloudflare_image_id })} alt=""/> : <PureFileIcon />}</div>
      <div className="product-table-type-title">
        <div>{original.title}</div>
        <div className="product-table-type-date">{original.uniqid}</div>
      </div>
      {original.pay_what_you_want ?
        <div className="mx-2 badge badge-sellix">
          {t('row.chooseYourPrice')}
        </div>
        : null}
      {original.isSubscription ?
        <div className="mx-2 badge badge-sellix">
          {t('row.subscription')}
        </div>
        : null}
    </div>
  }, [t]);

  const createButton = useCallback(({ getValue }: CellContext<ProductNormalized, unknown>) => {

    const affiliateLinkHost = `${customerPortalDomain ? 'https://' : ''}${customerPortalDomain ? customerPortalDomain : window.location.host}`;
    const affiliateLinkPath = isApproved ? `/a/${customerInfo.affiliate_custom_link}` : `?rcid=${customerInfo.id}`;
    const affiliateLink = `${affiliateLinkHost}${affiliateLinkPath}`;

    return <Tooltip tooltipText={t("copyProductLink")} name={`table-copied-${getValue()}`} successText={t("copied")} copiedText={`${affiliateLinkHost}/product/${getValue()}?rcid=${customerInfo.affiliate_custom_link || customerInfo.id}`}>
      <Button variant="secondary" className="affiliate-screen-button w-full">
        <CopyIcon grey={false} color="var(--purple6)" /> {t('row.getLink')}
      </Button>
    </Tooltip>
  }, [t, customerInfo]);

  const columns: ColumnDef<ProductNormalized>[] = useMemo(
    () => {

      const main = [
        {
          accessorKey: "product",
          header: t('table.columns.product') as string,
          cell: createProduct,
        }, {
          accessorKey: "affiliate_revenue_percent",
          header: t('table.columns.commission') as string,
          cell: createBalance,
        }, {
          accessorKey: "price_display",
          header: t('table.columns.youWillReceive') as string,
          cell: createPrice,
        }
      ];

      // if(customerPortalDomain) {
      //   return main
      // } else {
      return [...main, {
        accessorKey: "uniqid",
        header: t('table.columns.link') as string,
        cell: createButton,
        enableSorting: false,
        // className: "last-header-block",
      }]
      // }
    }, [createButton, createProduct, createBalance, createPrice, t, customerPortalDomain]);

  const isEmpty: boolean = !data.length;

  if(isEmpty) {
    return <Empty isProducts />
  }

  return <Table pageSize={10} data={data} columns={columns} className="last-right" />
}

export default AffiliateTable;
